import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  applyColorScheme,
  colorSchemeLabel,
  loadPrefs,
  nextColorScheme,
  savePrefs,
  type ColorScheme,
} from '../lib/board-prefs';

type Ctx = {
  scheme: ColorScheme;
  setScheme: (s: ColorScheme) => void;
  toggle: () => void;
  label: string;
};

const ThemeCtx = createContext<Ctx | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [scheme, setSchemeState] = useState<ColorScheme>(() => loadPrefs().colorScheme);

  useEffect(() => {
    applyColorScheme(scheme);
    savePrefs({ colorScheme: scheme });
  }, [scheme]);

  const value = useMemo<Ctx>(
    () => ({
      scheme,
      setScheme: setSchemeState,
      toggle: () => setSchemeState((s) => nextColorScheme(s)),
      label: colorSchemeLabel(scheme),
    }),
    [scheme],
  );

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme(): Ctx {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error('useTheme outside ThemeProvider');
  return ctx;
}
